import { equalsAny, equals } from './StringUtils';

/**
 * Convenience object for non-module TypeScript
 */
export const TypeUtils = {
  isNumber,
  isSignedInt,
  isUnsignedInt,
  isInt,
  isReal,
  isString,
  isBoolean,
  isDateTime,
  isIntValue,
  isNonNegativeIntValue,
  isNegativeIntValue,
  isRealValue,
  getShortType,
};

/**
 * Checks .Net type is Number or not
 * @example
 * isNumber("System.Int32")
 */
export function isNumber(type: string): boolean {
  return isInt(type) || isReal(type);
}

/**
 * @example
 * isSignedInt('System.String')
 */
export function isSignedInt(type: string): boolean {
  return equalsAny(getShortType(type), ['Int16', 'Int32', 'Int64', 'SByte'], true);
}

/**
 * @example
 * isUnsignedInt('System.UInt32')
 * isUnsignedInt('System.String')
 */
export function isUnsignedInt(type: string): boolean {
  return equalsAny(getShortType(type), ['UInt16', 'UInt32', 'UInt64', 'Byte'], true);
}

export function isInt(type: string): boolean {
  return isSignedInt(type) || isUnsignedInt(type);
}

export function isReal(type: string): boolean {
  return equalsAny(getShortType(type), ['Single', 'Double', 'Decimal'], true);
}

export function isString(type: string): boolean {
  return equals(getShortType(type), 'String', false);
}

export function isBoolean(type: string): boolean {
  return equals(getShortType(type), 'Boolean', true);
}

export function isDateTime(type: string): boolean {
  return equals(getShortType(type), 'DateTime', true);
}

export function isIntValue(str: string): boolean {
  const val = toNumber(str);
  return isFinite(val) && isInteger(val);
}

export function isNonNegativeIntValue(str: string): boolean {
  return isIntValue(str) && toNumber(str) >= 0;
}

export function isNegativeIntValue(str: string): boolean {
  return isIntValue(str) && toNumber(str) < 0;
}

export function isRealValue(value: string): boolean {
  return isFinite(toNumber(value));
}

function toNumber(value: string): number {
  //reason using '+' to convert string to number istead of parseInt/parseFloat functions
  // +"100a" = NaN
  // parseInt("100a") = 100
  return +value;
}

function isInteger(value: number): boolean {
  return typeof value === 'number' && isFinite(value) && Math.floor(value) === value;
}

export function getShortType(type: string): string {
  return (type || '').split('.').splice(-1)[0];
}

/**
 * @returns type string from a wrapped type name (after '#', currently), e.g. `"DataPair:#Foo"` yields `"Foo"`
 */
export function getWrappedTypeName(type: string) {
  let startIndex = Math.min(type.lastIndexOf('#') + 1, type.length - 1);
  return type.slice(startIndex);
}
